/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import "./EngineerCard.css";
import { Button, Badge, badgeVariantForLabel, Modal } from "./ui";

function EngineerCard({ engineer, onEdit, onDelete, isSelected, onSelect, onMemoClick, refreshTrigger }) {
  const { name, position, gender, skills, planner, engineer_status, phase, project, project_location } = engineer || {};
  const [isDragging, setIsDragging] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [memoCount, setMemoCount] = useState(0);
  const [memoStats, setMemoStats] = useState({ total: 0, urgent: 0, high: 0, pending: 0 });
  const [isFlipped, setIsFlipped] = useState(false);

  const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000;
  const now = Date.now();
  const isNew = engineer?.last_user_updated_at &&
    (now - new Date(engineer.last_user_updated_at).getTime()) < ONE_MONTH_MS;
  const isExtended = engineer?.contract_extended_at && (now - new Date(engineer.contract_extended_at).getTime()) < ONE_MONTH_MS;
  
  let skillsArray = [];
  if (Array.isArray(skills)) {
    skillsArray = skills;
  } else if (typeof skills === 'string') {
    skillsArray = skills.split(',').map(s => s.trim()).filter(Boolean);
  }

  const statusVariant = badgeVariantForLabel(engineer_status);

  const fetchMemoStats = async () => {
    if (!name) return;
    
    try {
      const response = await fetch(`/api/memos/by_engineer/?engineer_name=${encodeURIComponent(name)}`);
      if (response.ok) {
        const memos = await response.json();
        const urgent = memos.filter(memo => memo.priority === 'urgent' && !memo.is_completed).length;
        const high = memos.filter(memo => memo.priority === 'high' && !memo.is_completed).length;
        const pending = memos.filter(memo => !memo.is_completed).length;
        
        setMemoCount(memos.length);
        setMemoStats({
          total: memos.length,
          urgent: urgent,
          high: high,
          pending: pending
        });
      }
    } catch (error) {
      console.error('メモ統計取得エラー:', error);
    }
  };

  const getHighestPriority = () => {
    if (memoStats.urgent > 0) return 'urgent';
    if (memoStats.high > 0) return 'high';
    if (memoStats.pending > 0) return 'medium';
    if (memoStats.total > 0) return 'low';
    return 'none';
  };

  const getMemoRingClass = () => {
    const priority = getHighestPriority();
    if (memoCount === 0) return '';
    if (priority === 'urgent' || priority === 'high') return 'ring-2 ring-danger-border shadow-prodia-md';
    if (priority === 'medium') return 'ring-2 ring-warning-border shadow-prodia-md';
    return 'ring-2 ring-info-border shadow-prodia-md';
  };

  const getAvatarClass = () => {
    const priority = getHighestPriority();
    if (memoCount > 0) {
      if (priority === 'urgent' || priority === 'high') return 'bg-danger text-white';
      if (priority === 'medium') return 'bg-warning text-white';
      return 'bg-info text-white';
    }
    if (gender === 'female') return 'bg-prodia-primary text-prodia-primary-foreground';
    return 'bg-prodia-text-secondary text-white';
  };

  const getMemoBadgeVariant = () => {
    const priority = getHighestPriority();
    if (priority === 'urgent' || priority === 'high') return 'danger';
    if (priority === 'medium') return 'warning';
    return 'info';
  };

  const getMemoBadgeLabel = () => {
    const priority = getHighestPriority();
    if (priority === 'urgent') return '緊急メモあり';
    if (priority === 'high') return '重要メモあり';
    if (priority === 'medium') return '未完了あり';
    return 'メモあり';
  };

  useEffect(() => {
    fetchMemoStats();
  }, [name, refreshTrigger]);

  const handleMemoClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onMemoClick && onMemoClick(name);
  };

  const handleCardClick = (e) => {
    if (e.target.closest('button') || e.target.closest('input[type="checkbox"]') || e.target.closest('a')) return;
    setIsFlipped(prev => !prev);
  };

  const handleCardDoubleClick = (e) => {
    if (e.target.closest('button') || e.target.closest('input[type="checkbox"]') || e.target.closest('a')) return;
    setIsFlipped(false);
    onEdit(engineer);
  };

  const handleDragStart = (e) => {
    setIsDragging(true);
    e.dataTransfer.setData('engineer-id', engineer.id);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDeleteClick = () => {
    setShowConfirmDelete(true);
  };

  const confirmDelete = () => {
    onDelete(engineer.id);
    setShowConfirmDelete(false);
  };

  return (
    <>
      <div
        className={`engineer-card relative ${isDragging ? 'dragging' : ''} ${isFlipped ? 'flipped' : ''}`}
        draggable
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onClick={handleCardClick}
        onDoubleClick={handleCardDoubleClick}
      >
        <div
          className="absolute top-2 right-2 z-30"
          onClick={e => e.stopPropagation()}
          onDoubleClick={e => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => onSelect(engineer.id, e.target.checked)}
            className="w-5 h-5 text-prodia-primary rounded border-prodia-border focus:ring-prodia-primary/30"
          />
        </div>

        <div
          className="absolute top-2 left-2 z-30 flex gap-1"
          onClick={e => e.stopPropagation()}
          onDoubleClick={e => e.stopPropagation()}
        >
          <button
            onClick={() => onEdit(engineer)}
            className="bg-prodia-surface border border-prodia-border hover:bg-prodia-background text-prodia-text-secondary hover:text-prodia-primary rounded-full w-8 h-8 flex items-center justify-center text-sm transition-all shadow-prodia"
            title="編集"
          >
            <i className="fas fa-edit" />
          </button>
          <button
            onClick={handleDeleteClick}
            className="bg-prodia-surface border border-prodia-border hover:bg-danger-bg text-prodia-text-secondary hover:text-danger rounded-full w-8 h-8 flex items-center justify-center text-sm transition-all shadow-prodia"
            title="削除"
          >
            <i className="fas fa-trash" />
          </button>
        </div>

        {isSelected && (
          <div className="absolute inset-0 rounded-2xl ring-2 ring-prodia-primary z-20 pointer-events-none" />
        )}

        <div className="card-inner relative">
          <div className={`card-front rounded-2xl p-6 w-full min-h-[360px] flex flex-col items-center justify-between relative transition-all duration-300 ${getMemoRingClass()}`}>
            <div className="flex flex-col items-center flex-grow">
              <div className={`relative w-16 h-16 rounded-full flex items-center justify-center text-3xl mb-3 shadow-prodia transition-all ${getAvatarClass()}`}>
                <i className={gender === 'female' ? 'fas fa-user' : 'fas fa-user-tie'} />
                {memoCount > 0 && (
                  <div className="absolute -bottom-1 -right-1">
                    <i className={`fas fa-sticky-note text-sm ${
                      getHighestPriority() === 'urgent' || getHighestPriority() === 'high'
                        ? 'text-danger'
                        : getHighestPriority() === 'medium'
                          ? 'text-warning'
                          : 'text-info'
                    }`} />
                  </div>
                )}
              </div>

              <div className="text-center mb-2">
                <h3 className="text-lg font-bold text-prodia-text-primary mb-1">{name}</h3>
                {(isExtended || isNew) && (
                  <div className="flex items-center justify-center gap-1 mb-1">
                    {isExtended && (
                      <Badge variant="info" icon="fas fa-calendar-check">
                        延長
                      </Badge>
                    )}
                    {isNew && (
                      <Badge variant="primary">NEW</Badge>
                    )}
                  </div>
                )}
                {position && <span className="text-sm text-prodia-text-secondary font-medium block">{position}</span>}
                <div className="mt-2">
                  <Badge variant={statusVariant} showIcon>
                    {engineer_status || '不明'}
                  </Badge>
                </div>
                {memoCount > 0 && (
                  <div className="mt-2">
                    <Badge variant={getMemoBadgeVariant()} icon="fas fa-sticky-note">
                      {getMemoBadgeLabel()}
                    </Badge>
                  </div>
                )}
              </div>

              <div className="text-xs text-prodia-text-muted mb-2">担当プランナー: {planner}</div>
              <div className="text-xs text-info-foreground font-semibold mb-2">プロジェクト内容: {project || 'なし'}</div>
              {project_location && (
                <div className="text-xs text-prodia-text-secondary font-medium mb-2">
                  <i className="fas fa-map-marker-alt mr-1 text-prodia-text-muted" />
                  {project_location}
                </div>
              )}
            </div>

            <div className="w-full mt-auto">
              <div className="flex justify-center gap-2 items-center">
                <Button
                  size="sm"
                  variant={memoCount > 0 ? 'primary' : 'secondary'}
                  onClick={handleMemoClick}
                  title={memoCount > 0 ? `${memoCount}件のメモがあります` : 'メモを作成'}
                >
                  <i className="fas fa-sticky-note" />
                  メモ
                </Button>
                
                {memoCount > 0 && (
                  <div className="flex gap-1">
                    {getHighestPriority() === 'urgent' && memoStats.urgent > 0 && (
                      <Badge variant="danger" icon="fas fa-exclamation">
                        {memoStats.urgent}
                      </Badge>
                    )}
                    {getHighestPriority() === 'high' && memoStats.high > 0 && (
                      <Badge variant="danger" icon="fas fa-star">
                        {memoStats.high}
                      </Badge>
                    )}
                    {getHighestPriority() === 'medium' && memoStats.pending > 0 && (
                      <Badge variant="warning" icon="fas fa-clock">
                        {memoStats.pending}
                      </Badge>
                    )}
                    {getHighestPriority() === 'low' && (
                      <Badge variant="info" icon="fas fa-sticky-note">
                        {memoStats.total}
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="card-back rounded-2xl p-4 w-full min-h-[360px]">
            <div className="flex flex-col h-full justify-start">
              <div className="text-center mb-4">
                <h4 className="text-lg font-bold text-prodia-text-primary mb-3">スキル</h4>
                <div className="flex flex-wrap gap-2 justify-center mb-4">
                  {skillsArray.map((s, idx) => (
                    <Badge key={idx} variant="neutral">{s}</Badge>
                  ))}
                </div>
              </div>
              
              <div className="text-center">
                <h4 className="text-base font-bold text-prodia-text-primary mb-3">経験フェーズ</h4>
                <div className="flex flex-wrap gap-2 justify-center">
                  {['要件定義','基本設計','詳細設計','製造','テスト','運用・保守'].map((p, idx) => (
                    <Badge
                      key={idx}
                      variant={phase && phase.includes(p) ? 'primary' : 'neutral'}
                    >
                      {p}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={showConfirmDelete}
        onClose={() => setShowConfirmDelete(false)}
        title="削除確認"
        size="sm"
        primaryLabel="削除する"
        primaryVariant="danger"
        secondaryLabel="キャンセル"
        onPrimary={confirmDelete}
        onSecondary={() => setShowConfirmDelete(false)}
      >
        <div className="text-center py-2">
          <div className="text-4xl mb-4 text-warning">
            <i className="fas fa-exclamation-triangle" />
          </div>
          <p className="text-prodia-text-secondary">
            <span className="font-semibold text-danger">{name}</span> さんを削除しますか？
            <br />
            この操作は取り消せません。
          </p>
        </div>
      </Modal>
    </>
  );
}

export default EngineerCard;
